import { useTheme } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import { DASHBOARD_ROUTE, MANAGED_INVENTORY_ROUTES } from 'routes/constants';
import {
  setViewAsStaff,
  useGetUserQuery,
  useSignoutUserMutation,
} from 'store/slices/apiV1/user';
import { apiSlice } from 'store/slices/api';
import {
  AppBar,
  Toolbar,
  Box,
  Alert,
  GridLegacy,
  Typography,
} from '@mui/material';
import SegmentIconButton from 'ui-component/Segment/SegmentIconButton';
import {
  IconSettings,
  IconLogout,
  IconDeviceDesktop,
  IconBug,
  IconUserShield,
} from '@tabler/icons-react';
import { useDeviceContext } from 'hooks/useDeviceContext';
import UserAvatar from 'ui-component/extended/Avatars/UserAvatar';
import { logout } from 'store/actions';
import { useDispatch, useSelector } from 'store';
import { skipToken } from '@reduxjs/toolkit/query/react';
import useLocalStorage from 'hooks/useLocalStorage';
import {
  warehouseDefaults,
  warehouseStorageName,
} from 'types/warehouseSettings';
import { ALL_APPS, cofactrName } from 'constants/appConstants';
import { handleErr } from 'utils/functions';
import { useEffect, useState } from 'react';
import BugReportDialog from 'ui-component/BugReportDialog';
import { isCofactrUser } from 'utils/userPermissions';
import { NewVersionBanner } from 'ui-component/NewVersionBanner';
import { useNewVersion } from 'contexts/NewVersionContext';
import { useUpdateOrgData } from 'hooks/useUpdateOrgData';
import _ from 'lodash';
import NoPlanBanner from 'ui-component/NoPlanBanner';
import {
  WorkstationType,
  workstationTypeLabels,
  workstationTypeSpecifications,
} from 'types/clientV2/stockLocations';
import { PrinterLabelSize } from 'types/inventory';
import WarehouseHelpHub from 'ui-component/GlobalSearch/WarehouseHelpHub';

const TopAppBar = () => {
  const { activateWarehouse } = useUpdateOrgData();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const { showNewVersion } = useNewVersion();
  const { isLoggedIn } = useSelector((state) => state.session);
  const { viewAsStaff } = useSelector((state) => state.user);
  const { showNoPlanBanner } = useSelector((state) => state.utility);

  const { data: user } = useGetUserQuery(isLoggedIn ? undefined : skipToken);
  const theme = useTheme();
  const { desktopScreen } = useDeviceContext();
  const isCofactr = isCofactrUser({ user, viewAsStaff });

  const [signoutUser] = useSignoutUserMutation();
  const [bugReportDialogOpen, setBugReportDialogOpen] = useState(false);

  const renderStaffUserToggle =
    _.find(user?.orgs, ['id', user?.org])?.name === cofactrName;

  const handleLogout = async () => {
    try {
      await signoutUser().unwrap();
    } catch (err) {
      handleErr(err, (__: string) => {});
    } finally {
      dispatch(logout());
      dispatch(apiSlice.util.resetApiState());
    }
  };

  useEffect(() => activateWarehouse(), [activateWarehouse]);

  const [settings] = useLocalStorage(warehouseStorageName, warehouseDefaults);

  const hasFacility = settings?.facility;
  const hasWorkstation = settings?.currentWorkstation;

  const workstationSpecs = _.get(
    workstationTypeSpecifications,
    settings?.workstationType
  );

  let hasRequiredPrinter = true;
  if (
    !settings?.printer2212 &&
    workstationSpecs?.printerSizes.includes(PrinterLabelSize.THERMAL_0201)
  ) {
    hasRequiredPrinter = false;
  }
  if (
    !settings?.printer3020 &&
    workstationSpecs?.printerSizes.includes(PrinterLabelSize.THERMAL_0302)
  ) {
    hasRequiredPrinter = false;
  }
  if (
    !settings?.printer4060 &&
    workstationSpecs?.printerSizes.includes(PrinterLabelSize.THERMAL_0406)
  ) {
    hasRequiredPrinter = false;
  }
  if (
    !settings?.printer8511 &&
    workstationSpecs?.printerSizes.includes(PrinterLabelSize.DOCUMENT_8511)
  ) {
    hasRequiredPrinter = false;
  }

  const isNoLocalPrintView = _.includes(
    [MANAGED_INVENTORY_ROUTES.RECEIVE, MANAGED_INVENTORY_ROUTES.SHIPPING],
    pathname
  );
  return (
    <>
      {bugReportDialogOpen && (
        <BugReportDialog
          open={bugReportDialogOpen}
          onClose={() => setBugReportDialogOpen(false)}
        />
      )}
      {showNewVersion && <NewVersionBanner />}
      {showNoPlanBanner && <NoPlanBanner />}
      <AppBar position="sticky" color="primary" sx={{ top: 0, bottom: 'auto' }}>
        <Toolbar>
          <GridLegacy
            container
            columnSpacing={1.5}
            justifyContent="flex-start"
            alignItems="center"
            sx={{ width: '25%' }}
          >
            <GridLegacy item xs="auto">
              <UserAvatar
                user={user}
                sx={{
                  ...theme.typography.mediumAvatar,
                }}
              />
            </GridLegacy>
            <GridLegacy item xs sx={{ overflow: 'hidden' }}>
              {!!user && (
                <Typography
                  align="left"
                  variant="subtitle1"
                  noWrap
                  sx={{ fontWeight: 'bold', color: 'white' }}
                >
                  {user?.firstName || ''} {user?.lastName || ''}
                </Typography>
              )}
              {!!user?.title && (
                <Typography
                  align="left"
                  variant="subtitle2"
                  noWrap
                  sx={{ color: 'white' }}
                >
                  {user?.title}
                </Typography>
              )}
            </GridLegacy>
          </GridLegacy>
          {settings.currentWorkstation && (
            <Typography
              align="left"
              variant="h2"
              noWrap
              sx={{ color: 'white' }}
            >
              {settings.currentWorkstation.name}
              {settings.workstationType
                ? ` - ${
                    workstationTypeLabels[
                      settings.workstationType as WorkstationType
                    ]
                  }`
                : ''}
            </Typography>
          )}
          <Box sx={{ flexGrow: 1 }} />
          {(!hasFacility || !hasWorkstation) && (
            <Alert
              severity="error"
              variant="filled"
              icon={<IconSettings size={desktopScreen ? 32 : 24} />}
              onClick={() => navigate(MANAGED_INVENTORY_ROUTES.SETTINGS)}
              sx={{ cursor: 'pointer' }}
            >
              Select a Facility and Workstation
            </Alert>
          )}
          {!hasRequiredPrinter && (
            <Alert
              severity="error"
              variant="filled"
              icon={<IconSettings size={desktopScreen ? 32 : 24} />}
              onClick={() => navigate(MANAGED_INVENTORY_ROUTES.SETTINGS)}
              sx={{ cursor: 'pointer' }}
            >
              Select Printer(s)
            </Alert>
          )}
          {isNoLocalPrintView && settings.useBrowserPrint && (
            <Alert
              severity="error"
              variant="filled"
              icon={<IconSettings size={desktopScreen ? 32 : 24} />}
              onClick={() => navigate(MANAGED_INVENTORY_ROUTES.SETTINGS)}
              sx={{ cursor: 'pointer' }}
            >
              Browser Print Not Supported In This View
            </Alert>
          )}
          {workstationSpecs?.requiresLocalPrinting &&
            !settings.useBrowserPrint && (
              <Alert
                severity="error"
                variant="filled"
                icon={<IconSettings size={desktopScreen ? 32 : 24} />}
                onClick={() => navigate(MANAGED_INVENTORY_ROUTES.SETTINGS)}
                sx={{ cursor: 'pointer' }}
              >
                Browser Print Required For This View
              </Alert>
            )}
          <WarehouseHelpHub />
          {isCofactr && (
            <SegmentIconButton
              size={desktopScreen ? 'large' : 'medium'}
              color="inherit"
              onClick={() => setBugReportDialogOpen(true)}
            >
              <IconBug size={desktopScreen ? 32 : 24} color="white" />
            </SegmentIconButton>
          )}
          {renderStaffUserToggle && (
            <SegmentIconButton
              size={desktopScreen ? 'large' : 'medium'}
              color="inherit"
              onClick={() => {
                dispatch(setViewAsStaff(!viewAsStaff));
              }}
            >
              <IconUserShield
                size={desktopScreen ? 32 : 24}
                color={viewAsStaff ? theme.palette.success.main : 'white'}
              />
            </SegmentIconButton>
          )}
          {desktopScreen && (
            <SegmentIconButton
              size="large"
              color="inherit"
              onClick={() => navigate(DASHBOARD_ROUTE)}
            >
              <IconDeviceDesktop size={32} color="white" />
            </SegmentIconButton>
          )}
          <SegmentIconButton
            size={desktopScreen ? 'large' : 'medium'}
            color="inherit"
            onClick={() => navigate(MANAGED_INVENTORY_ROUTES.SETTINGS)}
            permissionScope={{ app: ALL_APPS.WAREHOUSE.id }}
          >
            <IconSettings size={desktopScreen ? 32 : 24} />
          </SegmentIconButton>
          <SegmentIconButton
            color="inherit"
            size={desktopScreen ? 'large' : 'medium'}
            onClick={handleLogout}
          >
            <IconLogout size={desktopScreen ? 32 : 24} />
          </SegmentIconButton>
        </Toolbar>
      </AppBar>
    </>
  );
};

export default TopAppBar;
